import axios from 'axios';
import type { AxiosInstance } from 'axios';
import type {
  Activity,
  ActivityLibraryItem,
  ActivityLibraryVisibility,
  ActivityResponseSummary,
  Achievement,
  ApiResponse,
  Assignment,
  Game,
  TeacherAchievementOverview,
  TeacherDashboardOverview,
  TeacherGameOverview,
  Unit,
  UnitStudent,
  User,
  UserAchievement,
} from '@/types';
import { useDemoStore } from '@/stores/demoStore';
import { handleDemoRequest } from '@/demo/demoHandlers';
import { API_BASE_URL } from '@/config/api';
import { createLabsReviewApi } from '@ludolio/shared-labs';
import type { UnitLabsSettings, UpdateUnitLabsSettingsInput } from '@ludolio/shared-units';

class ApiClient {
  public client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Enable sending cookies with requests
    });

    this.client.interceptors.request.use((config) => {
      if (useDemoStore.getState().isDemoMode) {
        config.adapter = async (cfg) => {
          const body = typeof cfg.data === 'string'
            ? (() => { try { return JSON.parse(cfg.data); } catch { return {}; } })()
            : (cfg.data ?? {});
          const data = handleDemoRequest(cfg.url || '', body);
          return { data, status: 200, statusText: 'OK', headers: {}, config: cfg, request: {} } as any;
        };
      }
      return config;
    });
  }

  // Token methods are kept for backward compatibility but are no-ops
  setToken(_token: string) {
    // Token is now stored in HttpOnly cookie by the backend
    // This method is kept for backward compatibility
  }

  clearToken() {
    // Token is cleared by the backend when logout is called
    // This method is kept for backward compatibility
  }

  getToken() {
    // Token is stored in HttpOnly cookie, not accessible from JS
    return null;
  }

  // Auth endpoints
  async sendOtp(email: string): Promise<ApiResponse<{ success: boolean; message: string; isNewUser: boolean }>> {
    // Use teacher-specific endpoint that validates email is a teacher or in whitelist
    const response = await this.client.post('/auth/teacher/send-otp', { email });
    return response.data;
  }

  async startInstitutionalLogin(email: string, target: string): Promise<ApiResponse<{
    action: 'redirect_sso';
    redirectUrl: string;
    provider: string;
  } | {
    action: 'otp_sent';
    success: boolean;
    message: string;
    isNewUser: boolean;
  }>> {
    const response = await this.client.post('/auth/institutional-login', { email, portal: 'teacher', client: 'web', target });
    return response.data;
  }

  async verifyOtp(email: string, token: string, firstName?: string, lastName?: string, orgUsername?: string): Promise<ApiResponse<{ user: User }>> {
    const payload: any = { email, token };
    if (firstName) {
      payload.firstName = firstName;
    }
    if (lastName) {
      payload.lastName = lastName;
    }
    if (orgUsername) {
      payload.orgUsername = orgUsername;
    }
    const response = await this.client.post('/auth/teacher/verify-otp', payload);
    return response.data;
  }

  async updateName(firstName: string, lastName: string, orgUsername?: string): Promise<ApiResponse<{ user: User }>> {
    const payload: any = { firstName, lastName };
    if (orgUsername !== undefined) {
      payload.orgUsername = orgUsername;
    }
    const response = await this.client.post('/auth/update-name', payload);
    return response.data;
  }

  async getOnboardingInfo(): Promise<ApiResponse<{ firstName: string; lastName: string; orgName: string | null; notice: { id: string; title: string; url: string } | null }>> {
    const response = await this.client.post('/auth/onboarding-info');
    return response.data;
  }

  async completeOnboarding(input: { firstName: string; lastName: string; marketingConsent: boolean; privacyAccepted: boolean; orgNoticeId?: string | null }): Promise<ApiResponse<{ user: User }>> {
    const response = await this.client.post('/auth/complete-onboarding', input);
    return response.data;
  }

  async acceptOrgNotice(orgNoticeId: string): Promise<ApiResponse<{ user: User }>> {
    const response = await this.client.post('/auth/accept-org-notice', { orgNoticeId });
    return response.data;
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.client.post('/auth/logout');
    this.clearToken();
    return response.data;
  }

  async validateToken(): Promise<ApiResponse<{ valid: boolean; user: User }>> {
    const response = await this.client.post('/auth/validate');
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.client.post('/auth/me');
    return response.data;
  }

  async checkEmailDomain(email: string): Promise<ApiResponse<{ found: boolean; organization: any; role: string | null }>> {
    const response = await this.client.post('/organizations/check-email-domain', { email });
    return response.data;
  }

  async getOrganization(organizationId: string): Promise<ApiResponse<{
    id: string;
    name: string;
    region?: string | null;
  }>> {
    const response = await this.client.post('/organizations/get', { organizationId });
    return response.data;
  }

  // Game endpoints
  async getAllGames(): Promise<ApiResponse<Game[]>> {
    const response = await this.client.post('/games/list', { origin: 'commercial' });
    return response.data;
  }

  async getGame(gameId: string): Promise<ApiResponse<Game>> {
    const response = await this.client.post('/games/get', { gameId });
    return response.data;
  }

  async createGame(gameData: Partial<Game>): Promise<ApiResponse<Game>> {
    const response = await this.client.post('/games/create', gameData);
    return response.data;
  }

  async updateGame(gameId: string, gameData: Partial<Game>): Promise<ApiResponse<Game>> {
    const response = await this.client.post('/games/update', { gameId, ...gameData });
    return response.data;
  }

  async deleteGame(gameId: string): Promise<ApiResponse<void>> {
    const response = await this.client.post('/games/delete', { gameId });
    return response.data;
  }

  // Achievement endpoints
  async createAchievement(achievementData: Partial<Achievement>): Promise<ApiResponse<Achievement>> {
    const response = await this.client.post('/achievements/create', achievementData);
    return response.data;
  }

  async getGameAchievements(gameId: string): Promise<ApiResponse<Achievement[]>> {
    const response = await this.client.post('/achievements/game-achievements', { gameId });
    return response.data;
  }

  async getUserAchievements(userId: string, gameId: string, unitId?: string): Promise<ApiResponse<UserAchievement[]>> {
    const response = await this.client.post('/achievements/user-achievements', { userId, gameId, unitId });
    return response.data;
  }

  async getAllUserAchievements(userId: string): Promise<ApiResponse<UserAchievement[]>> {
    const response = await this.client.post('/achievements/all-user-achievements', { userId });
    return response.data;
  }

  async getTeacherAchievementsOverview(): Promise<ApiResponse<TeacherAchievementOverview>> {
    const response = await this.client.post('/achievements/teacher-overview', {});
    return response.data;
  }

  // Assignment endpoints
  async createAssignment(assignmentData: {
    studentId: string;
    gameId: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
  }): Promise<ApiResponse<Assignment>> {
    const response = await this.client.post('/assignments/create', assignmentData);
    return response.data;
  }

  async getStudentGames(studentId: string): Promise<ApiResponse<Assignment[]>> {
    const response = await this.client.post('/assignments/student-games', { studentId });
    return response.data;
  }

  async updateAssignment(assignmentId: string, assignmentData: Partial<Assignment>): Promise<ApiResponse<Assignment>> {
    const response = await this.client.post('/assignments/update', { assignmentId, ...assignmentData });
    return response.data;
  }

  async deleteAssignment(assignmentId: string): Promise<ApiResponse<void>> {
    const response = await this.client.post('/assignments/delete', { assignmentId });
    return response.data;
  }

  async validateGameAccess(studentId: string, gameId: string): Promise<ApiResponse<{ hasAccess: boolean; assignment?: Assignment }>> {
    const response = await this.client.post('/assignments/validate-access', { studentId, gameId });
    return response.data;
  }

  // Student endpoints - Get students in the instructor's assigned units
  async getAllStudents(): Promise<ApiResponse<User[]>> {
    const response = await this.client.post('/users/list-students', { scope: 'units' });
    return response.data;
  }

  // Unit endpoints
  async createUnit(unitData: {
    code: string;
    name: string;
    year: number;
    semester: 'semester_1' | 'semester_2' | 'summer' | 'winter';
    metadata?: Record<string, any>;
  }): Promise<ApiResponse<Unit>> {
    const response = await this.client.post('/units/create', unitData);
    return response.data;
  }

  async getMyUnits(): Promise<ApiResponse<Unit[]>> {
    const response = await this.client.post('/units/my-units');
    return response.data;
  }

  async getUnitById(unitId: string): Promise<ApiResponse<Unit>> {
    const response = await this.client.post('/units/get', { unitId });
    return response.data;
  }

  async updateUnit(unitId: string, unitData: {
    code?: string;
    name?: string;
    year?: number;
    semester?: 'semester_1' | 'semester_2' | 'summer' | 'winter';
    isArchived?: boolean;
    metadata?: Record<string, any>;
  }): Promise<ApiResponse<Unit>> {
    const response = await this.client.post('/units/update', { unitId, ...unitData });
    return response.data;
  }

  async deleteUnit(unitId: string, confirmationCode: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/delete', { unitId, confirmationCode });
    return response.data;
  }

  async getUnitStudents(unitId: string): Promise<ApiResponse<UnitStudent[]>> {
    const response = await this.client.post('/units/students', { unitId });
    return response.data;
  }

  async addStudentToUnit(unitId: string, studentId: string): Promise<ApiResponse<any>> {
    const response = await this.client.post('/units/add-student', { unitId, studentId });
    return response.data;
  }

  async addStudentsByEmail(unitId: string, emails: string[]): Promise<ApiResponse<{
    added: any[];
    pending: string[];
    alreadyEnrolled: string[];
    alreadyPending: string[];
    invalidDomain: string[];
    invalidFormat: string[];
    invitationEmailFailed: string[];
    invitationBatchId: string | null;
    invitationJobsQueued: number;
  }>> {
    const response = await this.client.post('/units/add-students-by-email', { unitId, emails });
    return response.data;
  }

  async getInvitationBatchProgress(batchId: string): Promise<ApiResponse<{
    id: string;
    totalJobs: number;
    completedAt: string | null;
    counts: Record<string, number>;
  }>> {
    const response = await this.client.post('/units/invitation-email-batch-status', { batchId });
    return response.data;
  }

  async removeStudentFromUnit(unitId: string, studentId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/remove-student', { unitId, studentId });
    return response.data;
  }

  async removePendingEnrollment(unitId: string, pendingId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/remove-pending-enrollment', { unitId, pendingId });
    return response.data;
  }

  async resendStudentInvitation(
    unitId: string,
    pendingId: string
  ): Promise<ApiResponse<{ jobId: string | null; status: string; created: boolean }>> {
    const response = await this.client.post('/units/resend-student-invitation', { unitId, pendingId });
    return response.data;
  }

  // Teacher management endpoints
  async getUnitTeachers(unitId: string): Promise<ApiResponse<User[]>> {
    const response = await this.client.post('/units/teachers', { unitId });
    return response.data;
  }

  async addTeachersByEmail(unitId: string, emails: string[]): Promise<ApiResponse<{
    added: any[];
    pending: string[];
    alreadyPending: string[];
    notFound: string[];
    alreadyAssigned: string[];
    notTeacher: string[];
  }>> {
    const response = await this.client.post('/units/add-teachers-by-email', { unitId, emails });
    return response.data;
  }

  async removeTeacherFromUnit(unitId: string, teacherId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/remove-teacher', { unitId, teacherId });
    return response.data;
  }

  async getUnitLabsSettings(unitId: string): Promise<ApiResponse<UnitLabsSettings | null>> {
    const response = await this.client.post('/labs/units/settings/get', { unitId });
    return response.data;
  }

  async updateUnitLabsSettings(unitId: string, input: UpdateUnitLabsSettingsInput): Promise<ApiResponse<UnitLabsSettings>> {
    const response = await this.client.post('/labs/units/settings/update', { unitId, ...input });
    return response.data;
  }

  async removePendingUnitTeacher(unitId: string, pendingId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/remove-pending-teacher', { unitId, pendingId });
    return response.data;
  }

  async resendPendingUnitTeacherInvitation(
    unitId: string,
    pendingId: string
  ): Promise<ApiResponse<{ jobId: string | null; status: string; created: boolean }>> {
    const response = await this.client.post('/units/resend-pending-teacher-invitation', { unitId, pendingId });
    return response.data;
  }

  // Game management endpoints
  async getUnitGames(unitId: string): Promise<ApiResponse<Game[]>> {
    const response = await this.client.post('/units/games', { unitId });
    return response.data;
  }

  async addGameToUnit(unitId: string, gameId: string): Promise<ApiResponse<any>> {
    const response = await this.client.post('/units/add-game', { unitId, gameId });
    return response.data;
  }

  async removeGameFromUnit(unitId: string, gameId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/units/remove-game', { unitId, gameId });
    return response.data;
  }

  // Statistics endpoints
  async getUnitStatistics(unitId: string): Promise<ApiResponse<Array<{
    gameId: string;
    gameTitle: string;
    totalPlayingTimeSeconds: number;
    totalSessions: number;
    uniquePlayersCount: number;
    installationCount: number;
  }>>> {
    const response = await this.client.post('/statistics/unit-statistics', { unitId });
    return response.data;
  }

  async getTeacherDashboardOverview(): Promise<ApiResponse<TeacherDashboardOverview>> {
    const response = await this.client.post('/statistics/teacher-dashboard-overview', {});
    return response.data;
  }

  async getTeacherGamesOverview(): Promise<ApiResponse<TeacherGameOverview[]>> {
    const response = await this.client.post('/statistics/teacher-games-overview', {});
    return response.data;
  }

  async getRecentActivity(): Promise<ApiResponse<Array<{ student: string; action: string; game: string; time: string }>>> {
    const response = await this.client.post('/statistics/recent-activity', {});
    return response.data;
  }

  async getUnitGamePlayingTime(unitId: string, gameId: string): Promise<ApiResponse<Array<{
    userId: string;
    userName: string;
    totalPlayingTimeSeconds: number;
    sessionCount: number;
    lastPlayedAt: string | null;
  }>>> {
    const response = await this.client.post('/statistics/unit-game-playing-time', { unitId, gameId });
    return response.data;
  }

  async getUnitGameInstallations(unitId: string, gameId: string): Promise<ApiResponse<Array<{
    userId: string;
    userName: string;
    installationCount: number;
    lastInstalledAt: string | null;
  }>>> {
    const response = await this.client.post('/statistics/unit-game-installations', { unitId, gameId });
    return response.data;
  }

  async getStudentPlayingTimes(userId: string, unitId: string): Promise<ApiResponse<Array<{
    gameId: string;
    gameTitle: string;
    totalPlayingTimeSeconds: number;
    sessionCount: number;
    lastPlayedAt: string | null;
  }>>> {
    const response = await this.client.post('/statistics/user-playing-times', { userId, unitId });
    return response.data;
  }
  // Activity endpoints
  async createActivity(data: {
    unitId: string;
    gameId: string;
    achievementId: string;
    title: string;
    description?: string;
    type: 'quiz' | 'short_answer';
    config: {
      questions: Array<{ question: string; options?: string[]; correctAnswers?: string[] }>;
    };
  }): Promise<ApiResponse<Activity>> {
    const response = await this.client.post('/activities/create', data);
    return response.data;
  }

  async updateActivity(data: {
    activityId: string;
    title?: string;
    description?: string;
    type?: 'quiz' | 'short_answer';
    config?: {
      questions: Array<{ question: string; options?: string[]; correctAnswers?: string[] }>;
    };
    isActive?: boolean;
  }): Promise<ApiResponse<Activity>> {
    const response = await this.client.post('/activities/update', data);
    return response.data;
  }

  async deleteActivity(activityId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/activities/delete', { activityId });
    return response.data;
  }

  async listActivities(unitId: string): Promise<ApiResponse<Activity[]>> {
    const response = await this.client.post('/activities/list', { unitId });
    return response.data;
  }

  async getActivityResponses(activityId: string): Promise<ApiResponse<ActivityResponseSummary[]>> {
    const response = await this.client.post('/activities/responses', { activityId });
    return response.data;
  }

  async listActivityLibrary(): Promise<ApiResponse<ActivityLibraryItem[]>> {
    const response = await this.client.post('/activities/library', {});
    return response.data;
  }

  async createActivityLibraryItem(data: {
    gameId: string;
    achievementId: string;
    title: string;
    description?: string;
    type: 'quiz' | 'short_answer';
    config: {
      questions: Array<{ question: string; options?: string[]; correctAnswers?: string[] }>;
    };
    visibility: ActivityLibraryVisibility;
  }): Promise<ApiResponse<ActivityLibraryItem>> {
    const response = await this.client.post('/activities/library/create', data);
    return response.data;
  }

  async updateActivityLibraryItem(data: {
    libraryItemId: string;
    gameId?: string;
    achievementId?: string;
    title?: string;
    description?: string;
    type?: 'quiz' | 'short_answer';
    config?: {
      questions: Array<{ question: string; options?: string[]; correctAnswers?: string[] }>;
    };
    visibility?: ActivityLibraryVisibility;
    isArchived?: boolean;
  }): Promise<ApiResponse<ActivityLibraryItem>> {
    const response = await this.client.post('/activities/library/update', data);
    return response.data;
  }

  async archiveActivityLibraryItem(libraryItemId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.client.post('/activities/library/archive', { libraryItemId });
    return response.data;
  }

  async importActivityToUnit(libraryItemId: string, unitId: string): Promise<ApiResponse<Activity>> {
    const response = await this.client.post('/activities/import', { libraryItemId, unitId });
    return response.data;
  }
}

export const apiClient = new ApiClient();

export const labsReviewApi = createLabsReviewApi(async <T,>(url: string, body?: unknown) => {
  const response = await apiClient.client.post(url, body ?? {});
  return response.data?.data as T;
});

